import {
    type BulkPackagingTypes,
    type CargoPackagingTypes,
    isCargoPackagingType,
} from '#components/order/CreateOrderDialog/packagingOptions.tsx';
import type { BulkMaterial } from '#domain/BulkMaterial.ts';
import type { BulkMaterialUnitType } from '#domain/BulkMaterialUnitType.ts';
import type { CargoMaterial } from '#domain/CargoMaterial.ts';
import type { Comments } from '#domain/Comment.ts';
import type { Material } from '#domain/Material.ts';

import type {
    ApiBulkMaterialType,
    ApiBulkMaterialUnit,
    ApiCargoMaterial,
    ApiComments,
    ApiMaterials,
    ApiPackagingType,
    ApiWriteBulkMaterial,
    ApiWriteBulkMaterialUnit,
    ApiWriteCargoMaterial,
    ApiWriteCargoMaterialUnit,
    ApiWriteMaterial,
} from '../model/types.ts';

const v2CargoPackagingTypes: ApiPackagingType[] = [
    'unspecified',
    'unpackaged',
    'euro-pallet',
    'industrial-pallet',
    'one-way-pallet',
    'cage-pallet',
    'cardboard-box',
    'rack',
    'roll-container',
    'big-bag',
    'ibc',
    'coil',
    'transport-rack',
];

const v2BulkUnitTypeMap: Record<BulkMaterialUnitType, ApiBulkMaterialType> = {
    ton: 'ton',
    kilogram: 'kilogram',
    liter: 'liter',
    'cubic-meter': 'cubic-meter',
};

const mapCargoPackageType = (value: string | undefined): CargoPackagingTypes => {
    if (isCargoPackagingType(value)) {
        return value;
    }
    return 'unspecified';
};

const mapCargoPackageTypeToApi = (value: CargoPackagingTypes): ApiPackagingType => {
    return v2CargoPackagingTypes.includes(value as ApiPackagingType) ? (value as ApiPackagingType) : 'unspecified';
};

const isBulkUnitTypeKey = (value: string): value is BulkMaterialUnitType => value in v2BulkUnitTypeMap;

const mapBulkUnitTypeToApi = (value: string | undefined): ApiBulkMaterialType => {
    if (!value || !isBulkUnitTypeKey(value)) {
        console.warn(`Unknown bulk unit type: ${value}`);
        return 'kilogram';
    }
    return v2BulkUnitTypeMap[value];
};

const mapComments = (apiComments: ApiComments | undefined): Comments | undefined => {
    if (!apiComments) {
        return undefined;
    }
    return {
        loading: apiComments.loading,
        unloading: apiComments.unloading,
    };
};

const mapMaterialUnitValue = (apiProp: { target: number; loaded?: number; unloaded?: number }) => {
    return {
        ...apiProp,
    };
};

const mapOptionalMaterialUnitValue = (apiProp: { target?: number; loaded?: number; unloaded?: number } | undefined) => {
    if (!apiProp) {
        return undefined;
    }
    return {
        ...apiProp,
    };
};

const mapCargoMaterialUnit = (unit: ApiCargoMaterial['units'][number], index: number): CargoMaterial => ({
    id: unit.id,
    type: 'cargo-material',
    name: unit.name,
    packageType: mapCargoPackageType(unit.packaging_type),
    index,
    quantity: mapMaterialUnitValue(unit.quantity),
    weight: mapOptionalMaterialUnitValue(unit.weight),
    loadingMeter: mapOptionalMaterialUnitValue(unit.loading_meter),
    length: unit.dimensions?.length,
    width: unit.dimensions?.width,
    height: unit.dimensions?.height,
    comment: mapComments(unit.comments),
});

const mapBulkMaterialUnit = (unit: ApiBulkMaterialUnit, index: number): BulkMaterial => ({
    id: unit.id,
    type: 'bulk-material',
    packageType: 'bulk-material' as BulkPackagingTypes,
    name: unit.name,
    unitType: unit.unit_type ?? 'kilogram',
    quantity: mapMaterialUnitValue(unit.quantity),
    comment: mapComments(unit.comments),
    index,
});

export const mapMaterials = (materials?: ApiMaterials[]): Material[] => {
    if (!materials) {
        return [];
    }

    const mapped: Material[] = [];
    let index = 0;

    materials.forEach(material => {
        if (material.type === 'cargo-material') {
            material.units.forEach((unit: ApiCargoMaterial['units'][number]) => {
                mapped.push(mapCargoMaterialUnit(unit, index));
                index += 1;
            });
        }

        if (material.type === 'bulk-material') {
            material.units.forEach((unit: ApiBulkMaterialUnit) => {
                mapped.push(mapBulkMaterialUnit(unit, index));
                index += 1;
            });
        }
    });

    return mapped;
};

const buildDimensions = (material: CargoMaterial): ApiWriteCargoMaterialUnit['dimensions'] | undefined => {
    if (material.length === undefined || material.width === undefined || material.height === undefined) {
        return undefined;
    }
    return {
        length: material.length,
        width: material.width,
        height: material.height,
    };
};

const mapCargoMaterialToWriteUnit = (material: CargoMaterial): ApiWriteCargoMaterialUnit => {
    const dimensions = buildDimensions(material);

    return {
        name: material.name,
        packaging_type: mapCargoPackageTypeToApi(material.packageType),
        quantity: { target: material.quantity.target },
        weight: material.weight?.target !== undefined ? { target: material.weight.target } : undefined,
        loading_meter:
            material.loadingMeter?.target !== undefined ? { target: material.loadingMeter.target } : undefined,
        dimensions,
    };
};

const mapBulkMaterialToWriteUnit = (material: BulkMaterial): ApiWriteBulkMaterialUnit => ({
    name: material.name,
    unit_type: mapBulkUnitTypeToApi(material.unitType),
    quantity: { target: material.quantity.target },
});

export const mapMaterialToWriteMaterial = (material: Material): ApiWriteMaterial => {
    if (material.type === 'cargo-material') {
        return {
            type: 'cargo-material',
            units: [mapCargoMaterialToWriteUnit(material)],
        } satisfies ApiWriteCargoMaterial;
    }

    return {
        type: 'bulk-material',
        units: [mapBulkMaterialToWriteUnit(material)],
    } satisfies ApiWriteBulkMaterial;
};

export const mapMaterialsToWriteMaterials = (materials: Material[]): ApiWriteMaterial[] =>
    materials.map(mapMaterialToWriteMaterial);
